import { Button, Label, token } from "@may-i/mash-design-system";
import { PageSpinner } from "~/shared/ui/PageSpinner";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useUndoStack } from "~/shared/hooks/use-undo-stack";
import { useUnsavedChangesGuard } from "~/shared/hooks/use-unsaved-changes-guard";
import { compareCamId } from "~/shared/lib/compare-cam-id";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import type { CameraMemo } from "~/entities/camera-memo/types";
import type { Camera } from "~/entities/camera/types";
import { hasRealSnapshot } from "~/entities/camera/utils";
import { DeleteConfirmDialog } from "~/pages/resource-management/DeleteConfirmDialog";
import { readMappingSelection, writeMappingSelection } from "./lib/selected-mapping-storage";
import { useCurrentUser } from "~/features/auth/hooks/use-current-user";
import { BRANCHES_QUERY_KEY, useBranches } from "~/features/branch/hooks/use-branches";
import { BranchConfigStatus } from "~/entities/branch/types";
import { BranchConfigStatus as LegacyBranchConfigStatus } from "~/_legacy/types/common";
import { updateBranchConfig } from "~/_legacy/apis/update-branch-config";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { useUpdateCameraMemos } from "~/features/camera-memo/hooks/use-camera-memo-mutations";
import { useCameraMemos } from "~/features/camera-memo/hooks/use-camera-memos";
import { useCreateCamera, useUpdateCameraAdjustment, useUpdateCameraAnalysisNumber, useUpdateCameraMapping } from "~/features/camera/hooks/use-camera-mutations";
import { CAMERAS_QUERY_KEY, useCameras } from "~/features/camera/hooks/use-cameras";
import { useAnalysisConfigs } from "~/features/analysis-config/hooks/use-analysis-configs";
import type { AnalysisConfig } from "~/entities/analysis-config/types";
import { useAssignNNumbers } from "~/features/camera/hooks/use-assign-n-numbers";
import { useUpdateFloorPlan } from "~/features/floor-plan/hooks/use-floor-plan-mutations";
import { useFloorPlans } from "~/features/floor-plan/hooks/use-floor-plans";
import type { FloorPlanMemoDiagram, FloorPlanMemoText } from "~/_legacy/types/analysis";
import { DEFAULT_DIAGRAM_MEMO_STYLE, DEFAULT_TEXT_MEMO_STYLE, nextTempMemoId } from "./fabric-objects/FloorPlanMemo";
import { useCreateFloorPlanMemoDiagram } from "~/_legacy/data-hooks/use-create-floor-plan-memo-diagram";
import { useCreateFloorPlanMemoText } from "~/_legacy/data-hooks/use-create-floor-plan-memo-text";
import { useGetFloorPlanMemoDiagramsGroupedByFloorPlan, FLOOR_PLAN_MEMO_DIAGRAMS_GROUPED_BY_FLOOR_PLAN_QUERY_KEY } from "~/_legacy/data-hooks/use-get-floor-plan-memo-diagrams-grouped-by-floor-plan";
import { useGetFloorPlanMemoTextsGroupedByFloorPlan, FLOOR_PLAN_MEMO_TEXTS_GROUPED_BY_FLOOR_PLAN_QUERY_KEY } from "~/_legacy/data-hooks/use-get-floor-plan-memo-texts-grouped-by-floor-plan";
import { useUpdateFloorPlanMemoDiagram } from "~/_legacy/data-hooks/use-update-floor-plan-memo-diagram";
import { useUpdateFloorPlanMemoText } from "~/_legacy/data-hooks/use-update-floor-plan-memo-text";
import { useDeleteFloorPlanMemoDiagram } from "~/_legacy/data-hooks/use-delete-floor-plan-memo-diagram";
import { useDeleteFloorPlanMemoText } from "~/_legacy/data-hooks/use-delete-floor-plan-memo-text";
import { useQueryClient } from "@tanstack/react-query";
import { TEMP_IMAGE_URL } from "~/shared/ui/EmptyImagePlaceholder";
import { useToast } from "~/shared/ui/toast";
import { InlineMemoSection } from "~/pages/analysis-draft/InlineMemoSection";
import { ActionBar } from "./ActionBar";
import { SaveLoadingOverlay } from "./SaveLoadingOverlay";
import { CameraSidebar } from "./CameraSidebar";
import { FloorPlanCanvas } from "./FloorPlanCanvas";
import type { MemoAction } from "./MemoPanel";

const DEFAULT_ICON_SCALE = 0.6;
// 라벨 배수는 서버 기본값(label_size=1)과 맞춰야 기존 렌더가 그대로 유지된다
const DEFAULT_LABEL_SCALE = 1;
let tempCameraSeq = 0;
let tempMemoSeq = 0;

/**
 * Pending memo actions를 통합(squash)하여 서버에 보낼 최소한의 actions로 변환.
 * - 같은 메모에 대한 여러 update → 마지막 update만 유지
 * - create 후 update → create로 병합 (최종 데이터로)
 * - create 후 delete → 둘 다 제거 (서버에 아무것도 안 보냄)
 * - update 후 delete → delete만 유지
 * - 임시 ID(_tempId)를 제거하여 서버 API 형식으로 변환
 */
type CreateDataWithTempId = Omit<CameraMemo, "id" | "updatedAt"> & { _tempId: number };

interface SaveResult {
  failed: number;
  total: number;
  /** 서버가 알려준 첫 실패 사유 — 부분 실패 토스트에 그대로 덧붙여 원인을 알 수 있게 한다 */
  firstErrorMessage?: string;
}

type InternalAction =
  | { actionType: "create"; data: CreateDataWithTempId }
  | { actionType: "update"; data: CameraMemo }
  | { actionType: "delete"; data: CameraMemo };

function squashMemoActions(actions: MemoAction[]): MemoAction[] {
  const stateMap = new Map<number, { origin: "server" | "local"; action: InternalAction }>();

  for (const action of actions) {
    if (action.actionType === "create") {
      const data = action.data as CreateDataWithTempId;
      stateMap.set(data._tempId, { origin: "local", action: { actionType: "create", data } });
    } else if (action.actionType === "update") {
      const id = action.data.id;
      const existing = stateMap.get(id);
      if (existing?.origin === "local" && existing.action.actionType === "create") {
        // create 후 update → create로 병합 (최종 데이터로)
        const origData = existing.action.data;
        stateMap.set(id, {
          origin: "local",
          action: {
            actionType: "create",
            data: {
              cameraId: action.data.cameraId,
              type: action.data.type,
              content: action.data.content,
              floorPlanId: action.data.floorPlanId,
              positionForFloorPlan: action.data.positionForFloorPlan,
              createdAt: action.data.createdAt,
              _tempId: origData._tempId,
            },
          },
        });
      } else {
        stateMap.set(id, { origin: "server", action });
      }
    } else if (action.actionType === "delete") {
      const id = action.data.id;
      const existing = stateMap.get(id);
      if (existing?.origin === "local") {
        // create 후 delete → 아무것도 안 보냄
        stateMap.delete(id);
      } else {
        stateMap.set(id, { origin: "server", action });
      }
    }
  }

  const result: MemoAction[] = [];
  for (const { action } of stateMap.values()) {
    if (action.actionType === "create") {
      const { _tempId, ...data } = action.data;
      result.push({ actionType: "create", data });
    } else {
      result.push(action);
    }
  }
  return result;
}

export function CameraMappingPage() {
  const { t } = useTranslation("camera-mapping");
  const { t: tShared } = useTranslation("shared");
  const { showToast } = useToast();
  const { branchUid } = useParams();
  const navigate = useNavigate();
  const { selectedVersion } = useSelectedVersion();
  const versionId = selectedVersion?.id;
  const { data: currentUser } = useCurrentUser();
  const isStaff = currentUser?.isStaff ?? false;
  // 7차 aw: 현재 브랜치의 configStatus 를 알아야 단계 완료 처리 시 monotonic 으로 transition 가능
  const { data: branches } = useBranches();
  const currentBranch = useMemo(() => branches?.find((b) => b.uid === branchUid), [branches, branchUid]);

  const { data: serverCameras = [], isPending: isCamerasPending } = useCameras(branchUid, versionId);
  // F10: 이벤트 소스 타입(라인/구역) 필터용 — 카메라별 분석설정(라인/구역) 데이터
  const { data: analysisConfigs = [] } = useAnalysisConfigs(branchUid, versionId);
  const configByCameraId = useMemo(
    () => new Map<number, AnalysisConfig>(analysisConfigs.map((c) => [c.cameraId, c])),
    [analysisConfigs],
  );
  // F9: 카메라 목록 필터 결과(보이는 카메라 id 집합) — null = 필터 미적용(전체). 도면 캔버스에도 동일 적용.
  const [visibleCameraIds, setVisibleCameraIds] = useState<Set<number> | null>(null);
  // 자동 N번호 부여는 admin only — 고객사 진입 시 BE 403 회피 + 노이즈 toast 차단
  useAssignNNumbers(isStaff ? serverCameras : [], branchUid, versionId, {
    onErrors: (failed) => {
      showToast({
        type: "error",
        title: tShared("toast.assignNNumberError.title"),
        content: tShared("toast.assignNNumberError.content", { failed }),
      });
    },
  });
  const { data: floorPlans = [], isPending: isFloorPlansPending } = useFloorPlans(branchUid, versionId);
  const { data: cameraMemos = [] } = useCameraMemos(branchUid, versionId);

  const floorPlanIds = useMemo(() => floorPlans.map((fp) => fp.id), [floorPlans]);
  const { data: floorPlanMemoDiagramsByFp = {} } =
    useGetFloorPlanMemoDiagramsGroupedByFloorPlan({
      branchUid,
      branchVersionId: versionId,
      floorPlanIds,
    });
  const { data: floorPlanMemoTextsByFp = {} } =
    useGetFloorPlanMemoTextsGroupedByFloorPlan({
      branchUid,
      branchVersionId: versionId,
      floorPlanIds,
    });
  const { updateFloorPlanMemoDiagram } = useUpdateFloorPlanMemoDiagram({
    branchUid,
    branchVersionId: versionId,
  });
  const { updateFloorPlanMemoText } = useUpdateFloorPlanMemoText({
    branchUid,
    branchVersionId: versionId,
  });
  const { deleteFloorPlanMemoDiagram } = useDeleteFloorPlanMemoDiagram({
    branchUid,
    branchVersionId: versionId,
  });
  const { deleteFloorPlanMemoText } = useDeleteFloorPlanMemoText({
    branchUid,
    branchVersionId: versionId,
  });
  const { createFloorPlanMemoDiagram } = useCreateFloorPlanMemoDiagram({
    branchUid,
    branchVersionId: versionId,
  });
  const { createFloorPlanMemoText } = useCreateFloorPlanMemoText({
    branchUid,
    branchVersionId: versionId,
  });
  const queryClient = useQueryClient();

  // 도면 메모 변경 사항을 로컬에서 모아두고 saveMappings 시점에 일괄 적용 (legacy 방식과 동일)
  type PendingDiagramChange =
    | { type: "delete"; floorPlanId: number }
    | {
        type: "edit";
        floorPlanId: number;
        coordinates: { x: number; y: number }[];
        style: { fill?: string; borderColor?: string; opacity: number };
      }
    | {
        type: "create";
        floorPlanId: number;
        coordinates: { x: number; y: number }[];
        style: { fill?: string; borderColor?: string; opacity: number };
      };
  type PendingTextChange =
    | { type: "delete"; floorPlanId: number }
    | {
        type: "edit";
        floorPlanId: number;
        content: string;
        position: { x: number; y: number };
        style: FloorPlanMemoText["style"];
      }
    | {
        type: "create";
        floorPlanId: number;
        content: string;
        position: { x: number; y: number };
        style: FloorPlanMemoText["style"];
      };
  const [pendingDiagramChanges, setPendingDiagramChanges] = useState<Map<number, PendingDiagramChange>>(new Map());
  const [pendingTextChanges, setPendingTextChanges] = useState<Map<number, PendingTextChange>>(new Map());

  // 되돌리기 스냅샷은 상태 선언이 모두 끝난 뒤에야 만들 수 있는데(아래 useUndoStack),
  // 도면 메모 핸들러들은 그보다 위에 있다. ref 로 연결해 선언 순서 문제를 피한다.
  const pushUndoRef = useRef<() => void>(() => {});

  const handleMemoDiagramModified = useCallback(
    (
      memoId: number,
      floorPlanId: number,
      coordinates: { x: number; y: number }[],
      style: { fill?: string; borderColor?: string; opacity: number },
    ) => {
      // object:modified — fabric 이 드래그/크기변경이 끝날 때 한 번만 발생시킨다.
      // 프레임마다가 아니므로 동작 하나 = 되돌리기 하나가 된다.
      pushUndoRef.current();
      setPendingDiagramChanges((prev) => {
        const existing = prev.get(memoId);
        const next = new Map(prev);
        // 임시(create) 항목을 수정한 경우는 create 유지하고 데이터만 갱신 (음수 ID로 PATCH 가는 사고 방지)
        if (memoId < 0 && existing?.type === "create") {
          next.set(memoId, { type: "create", floorPlanId, coordinates, style });
        } else {
          next.set(memoId, { type: "edit", floorPlanId, coordinates, style });
        }
        return next;
      });
    },
    [],
  );

  const handleMemoTextModified = useCallback(
    (
      memoId: number,
      floorPlanId: number,
      content: string,
      position: { x: number; y: number },
      style: FloorPlanMemoText["style"],
    ) => {
      pushUndoRef.current();
      setPendingTextChanges((prev) => {
        const existing = prev.get(memoId);
        const next = new Map(prev);
        if (memoId < 0 && existing?.type === "create") {
          next.set(memoId, { type: "create", floorPlanId, content, position, style });
        } else {
          next.set(memoId, { type: "edit", floorPlanId, content, position, style });
        }
        return next;
      });
    },
    [],
  );

  const handleMemoDiagramDelete = useCallback(
    (memoId: number, floorPlanId: number) => {
      pushUndoRef.current();
      setPendingDiagramChanges((prev) => {
        // 임시 ID는 그냥 pending에서 제거
        if (memoId < 0) {
          if (!prev.has(memoId)) return prev;
          const next = new Map(prev);
          next.delete(memoId);
          return next;
        }
        const next = new Map(prev);
        next.set(memoId, { type: "delete", floorPlanId });
        return next;
      });
    },
    [],
  );

  const handleMemoTextDelete = useCallback(
    (memoId: number, floorPlanId: number) => {
      pushUndoRef.current();
      setPendingTextChanges((prev) => {
        // 임시 ID(아직 서버에 없음)를 삭제하면 그냥 pending에서 제거
        if (memoId < 0) {
          if (!prev.has(memoId)) return prev;
          const next = new Map(prev);
          next.delete(memoId);
          return next;
        }
        const next = new Map(prev);
        next.set(memoId, { type: "delete", floorPlanId });
        return next;
      });
    },
    [],
  );

  const handleMemoTextCreate = useCallback(
    (floorPlanId: number, position: { x: number; y: number }) => {
      pushUndoRef.current();
      const tempId = nextTempMemoId();
      setPendingTextChanges((prev) => {
        const next = new Map(prev);
        next.set(tempId, {
          type: "create",
          floorPlanId,
          content: t("floorPlanMemo.defaultTextContent", { ns: "shared" }),
          position,
          style: DEFAULT_TEXT_MEMO_STYLE,
        });
        return next;
      });
      return tempId;
    },
    [],
  );

  const handleMemoDiagramCreate = useCallback(
    (floorPlanId: number, coordinates: { x: number; y: number }[]) => {
      pushUndoRef.current();
      const tempId = nextTempMemoId();
      setPendingDiagramChanges((prev) => {
        const next = new Map(prev);
        next.set(tempId, {
          type: "create",
          floorPlanId,
          coordinates,
          style: DEFAULT_DIAGRAM_MEMO_STYLE,
        });
        return next;
      });
      return tempId;
    },
    [],
  );

  // 패널에서 style 변경 — 좌표는 그대로 두고 style만 갱신해 pendingChanges에 합류
  const handleMemoTextStyleChange = useCallback(
    (memoId: number, partial: Partial<{ color: string; width: number; opacity: number; fontSize: number; hasStroke: boolean }>) => {
      setPendingTextChanges((prev) => {
        const existing = prev.get(memoId);
        const next = new Map(prev);
        if (existing && existing.type !== "delete") {
          // 기존 entry 데이터 + partial style merge
          const mergedStyle = { ...existing.style, ...partial };
          next.set(memoId, { ...existing, style: mergedStyle });
        } else {
          // 서버 origin 메모를 처음 수정 — edit type 새로 생성. 좌표/내용은 fabric 객체에서 추출 안 하고 placeholder로 두면 안 되니, 서버 데이터 기반.
          const serverFp = Object.entries(floorPlanMemoTextsByFp).find(([, list]) =>
            (list as Array<{ id: number }>)?.some((m) => m.id === memoId),
          );
          if (!serverFp) return prev;
          const fpId = Number(serverFp[0]);
          const memo = (serverFp[1] as Array<{ id: number; content: string; position: { x: number; y: number }; style: typeof partial }>)
            .find((m) => m.id === memoId);
          if (!memo) return prev;
          next.set(memoId, {
            type: "edit",
            floorPlanId: fpId,
            content: memo.content,
            position: memo.position,
            style: { ...memo.style, ...partial } as { color: string; width: number; opacity: number; fontSize: number; hasStroke: boolean },
          });
        }
        return next;
      });
    },
    [floorPlanMemoTextsByFp],
  );

  const handleMemoDiagramStyleChange = useCallback(
    (memoId: number, partial: Partial<{ fill?: string; borderColor?: string; opacity: number }>) => {
      setPendingDiagramChanges((prev) => {
        const existing = prev.get(memoId);
        const next = new Map(prev);
        if (existing && existing.type !== "delete") {
          const mergedStyle = { ...existing.style, ...partial };
          next.set(memoId, { ...existing, style: mergedStyle });
        } else {
          const serverFp = Object.entries(floorPlanMemoDiagramsByFp).find(([, list]) =>
            (list as Array<{ id: number }>)?.some((m) => m.id === memoId),
          );
          if (!serverFp) return prev;
          const fpId = Number(serverFp[0]);
          const memo = (serverFp[1] as Array<{ id: number; coordinates: { x: number; y: number }[]; style: typeof partial }>)
            .find((m) => m.id === memoId);
          if (!memo) return prev;
          next.set(memoId, {
            type: "edit",
            floorPlanId: fpId,
            coordinates: memo.coordinates,
            style: { ...memo.style, ...partial } as { fill?: string; borderColor?: string; opacity: number },
          });
        }
        return next;
      });
    },
    [floorPlanMemoDiagramsByFp],
  );

  const updateCameraMemos = useUpdateCameraMemos(branchUid!, versionId);

  const [tempCameras, setTempCameras] = useState<Camera[]>([]);
  // PO QA 3차: 도면 fit 가용 영역 = 캔버스 - (InlineMemoOverlay 상단 영역 + ActionBar 하단 영역).
  // 분석초안 도면 단독 보기와 동일한 사이즈. callback ref 로 mount/unmount 시점 정확히 잡기.
  const [inlineMemoOverlayHeight, setInlineMemoOverlayHeight] = useState(0);
  const inlineMemoOverlayRoRef = useRef<ResizeObserver | null>(null);
  // InlineMemoOverlay 가 `top: 48px` 부터 떠 있으므로 가리는 실제 영역의 bottom = offsetTop + offsetHeight
  const inlineMemoOverlayRef = useCallback((el: HTMLDivElement | null) => {
    if (inlineMemoOverlayRoRef.current) {
      inlineMemoOverlayRoRef.current.disconnect();
      inlineMemoOverlayRoRef.current = null;
    }
    if (!el) {
      // overlay unmount (카메라 선택 해제) — padding 도 0 으로
      setInlineMemoOverlayHeight(0);
      return;
    }
    const measure = () => setInlineMemoOverlayHeight(el.offsetTop + el.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    inlineMemoOverlayRoRef.current = ro;
  }, []);
  const [bottomBarHeight, setBottomBarHeight] = useState(0);
  const bottomBarRoRef = useRef<ResizeObserver | null>(null);
  const bottomBarRef = useCallback((el: HTMLDivElement | null) => {
    if (bottomBarRoRef.current) {
      bottomBarRoRef.current.disconnect();
      bottomBarRoRef.current = null;
    }
    if (!el) return;
    setBottomBarHeight(el.offsetHeight);
    const ro = new ResizeObserver(() => setBottomBarHeight(el.offsetHeight));
    ro.observe(el);
    bottomBarRoRef.current = ro;
  }, []);
  const cameras = useMemo(
    () => [...serverCameras, ...tempCameras],
    [serverCameras, tempCameras],
  );

  const createCamera = useCreateCamera(branchUid!, versionId);
  const updateCameraMapping = useUpdateCameraMapping(branchUid!, versionId);
  const updateCameraAdjustment = useUpdateCameraAdjustment(branchUid!, versionId);
  const updateFloorPlan = useUpdateFloorPlan(branchUid!, versionId);
  const updateAnalysisNumber = useUpdateCameraAnalysisNumber(branchUid!, versionId);

  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<number | null>(
    null,
  );
  const selectedFloorPlan = floorPlans.find((fp) => fp.id === selectedFloorPlanId);

  // 서버 데이터 + 로컬 변경 사항(편집·삭제) + 신규 생성 항목을 합쳐서 렌더링용 데이터 생성
  const visibleDiagramsForCurrentFloor = useMemo<FloorPlanMemoDiagram[] | undefined>(() => {
    if (selectedFloorPlanId == null) return undefined;
    const server: FloorPlanMemoDiagram[] = floorPlanMemoDiagramsByFp[selectedFloorPlanId] ?? [];
    const result: FloorPlanMemoDiagram[] = [];
    for (const d of server) {
      const change = pendingDiagramChanges.get(d.id);
      if (!change) {
        result.push(d);
        continue;
      }
      if (change.type === "delete") continue;
      if (change.type === "edit") {
        result.push({ ...d, coordinates: change.coordinates, style: change.style });
      }
    }
    for (const [memoId, change] of pendingDiagramChanges) {
      if (memoId < 0 && change.type === "create" && change.floorPlanId === selectedFloorPlanId) {
        result.push({ id: memoId, floorPlanId: change.floorPlanId, coordinates: change.coordinates, style: change.style });
      }
    }
    return result;
  }, [floorPlanMemoDiagramsByFp, selectedFloorPlanId, pendingDiagramChanges]);

  const visibleTextsForCurrentFloor = useMemo<FloorPlanMemoText[] | undefined>(() => {
    if (selectedFloorPlanId == null) return undefined;
    const server: FloorPlanMemoText[] = floorPlanMemoTextsByFp[selectedFloorPlanId] ?? [];
    const result: FloorPlanMemoText[] = [];
    for (const t of server) {
      const change = pendingTextChanges.get(t.id);
      if (!change) {
        result.push(t);
        continue;
      }
      if (change.type === "delete") continue;
      if (change.type === "edit") {
        result.push({ ...t, content: change.content, position: change.position, style: change.style });
      }
    }
    for (const [memoId, change] of pendingTextChanges) {
      if (memoId < 0 && change.type === "create" && change.floorPlanId === selectedFloorPlanId) {
        result.push({ id: memoId, floorPlanId: change.floorPlanId, content: change.content, position: change.position, style: change.style });
      }
    }
    return result;
  }, [floorPlanMemoTextsByFp, selectedFloorPlanId, pendingTextChanges]);

  const [iconScale, setIconScale] = useState<number>(DEFAULT_ICON_SCALE);
  const [labelScale, setLabelScale] = useState<number>(DEFAULT_LABEL_SCALE);
  const [memoPlacingMode, setMemoPlacingMode] = useState<{ memoId: number; cameraId: number } | null>(null);
  const [selectedMemoId, setSelectedMemoId] = useState<number | null>(null);
  // 메모는 즉시 저장이지만, 아직 저장되지 않은 신규 카메라(임시 음수 id)에 단 메모만은
  // 서버가 받을 수 있는 cameraId 가 없어 즉시 보낼 수 없다. 이 큐에만 담아두고
  // 카메라 저장 시 실제 id 로 바꿔 함께 보낸다.
  const [pendingTempCameraMemoActions, setPendingTempCameraMemoActions] = useState<MemoAction[]>([]);
  // 다른 도면에 이미 배치된 카메라를 현재 도면에 배치하려 할 때 확인 다이얼로그
  const [placementConflict, setPlacementConflict] = useState<{
    cameraId: number;
    position: { x: number; y: number; rotation: number };
    fromFloorPlanId: number;
  } | null>(null);

  // 화각 조정 — 사용자가 명시적으로 시작한 카메라의 조정 상태(서버 adjustment_status 와 별도 로컬 큐)
  // null = 서버에 저장된 조정 상태를 제거 예정. 객체 = 새 조정 추가/업데이트 예정.
  // committed: "조정 완료" 버튼으로 명시 완료 했는지 (PO QA 2차 j-1 B안) — false 면 저장 대상에서 제외.
  type AdjustmentInfo = {
    adjustmentId: string;
    position: { x: number; y: number; rotation: number };
    frame: { dx: number; dy: number };
    committed: boolean;
  };
  const [pendingAdjustments, setPendingAdjustments] = useState<Map<number, AdjustmentInfo | null>>(new Map());
  // 조정 모드 (실제 편집 중) — 한 번에 한 카메라만. null 이면 모두 "조정 완료/안함" 상태
  const [adjustmentEditingCameraId, setAdjustmentEditingCameraId] = useState<number | null>(null);

  // 마커 툴바 "메모 추가" 버튼 → 사이드바 메모 패널 열기 시그널
  const [memoOpenSignal, setMemoOpenSignal] = useState(0);
  const handleAddMemoFromToolbar = useCallback((cameraId: number) => {
    setSelectedCameraId(cameraId);
    setMemoOpenSignal((n) => n + 1);
  }, []);

  // 저장된 메모는 즉시 저장 + 낙관적 업데이트라 캐시(cameraMemos)가 이미 최신이다.
  // 여기서 얹는 건 아직 저장 못 한 신규 카메라 메모뿐이다.
  const effectiveMemos = useMemo(() => {
    if (pendingTempCameraMemoActions.length === 0) return cameraMemos;
    let result = [...cameraMemos];
    for (const action of pendingTempCameraMemoActions) {
      if (action.actionType === "create") {
        const data = action.data as Omit<CameraMemo, "id" | "updatedAt"> & { _tempId: number };
        result.push({
          id: data._tempId,
          cameraId: data.cameraId,
          type: data.type,
          content: data.content,
          floorPlanId: data.floorPlanId,
          positionForFloorPlan: data.positionForFloorPlan,
          createdAt: data.createdAt,
          updatedAt: Date.now(),
        });
      } else if (action.actionType === "update") {
        const idx = result.findIndex((m) => m.id === action.data.id);
        if (idx !== -1) result[idx] = action.data;
      } else if (action.actionType === "delete") {
        result = result.filter((m) => m.id !== action.data.id);
      }
    }
    return result;
  }, [cameraMemos, pendingTempCameraMemoActions]);

  // M1: 메모 선택 시 그 메모가 배치된 도면으로 전환 (카메라 클릭과 동일한 네비 — 메모가
  // 선택 카메라와 다른 도면에 있을 때 클릭이 무반응이던 문제 해결). 미배치 메모는 전환 안 함.
  const handleMemoSelect = useCallback(
    (memoId: number | null) => {
      setSelectedMemoId(memoId);
      if (memoId == null) return;
      const memo = effectiveMemos.find((m) => m.id === memoId);
      if (
        memo &&
        memo.floorPlanId != null &&
        memo.positionForFloorPlan != null &&
        memo.floorPlanId !== selectedFloorPlanId
      ) {
        setSelectedFloorPlanId(memo.floorPlanId);
      }
    },
    [effectiveMemos, selectedFloorPlanId],
  );

  // 아이콘/라벨 크기는 "보기 설정"이라 페이지 저장 버튼과 분리 — 슬라이더에서 손을 뗀 시점에 바로 저장.
  // (드래그 중엔 onChange 로 로컬 상태만 갱신 → 요청 폭주 방지)
  const commitFloorPlanScale = useCallback(
    (params: { iconSize?: number; labelSize?: number }) => {
      if (selectedFloorPlanId == null) return;
      updateFloorPlan.mutate({ floorPlanId: selectedFloorPlanId, ...params });
    },
    [selectedFloorPlanId, updateFloorPlan],
  );

  // 도면 변경 시 iconScale / labelScale 초기화
  useEffect(() => {
    if (selectedFloorPlan) {
      setIconScale(selectedFloorPlan.iconSize || DEFAULT_ICON_SCALE);
      setLabelScale(selectedFloorPlan.labelSize || DEFAULT_LABEL_SCALE);
    }
  }, [selectedFloorPlan?.id]);

  const [localMappings, setLocalMappings] = useState<
    Map<
      number,
      {
        floorPlanId: number;
        position: { x: number; y: number; rotation: number };
        // "default" = 단순 이동(위치만 변경, 서버 type 보존). "add"/"edit" 는 명시적 타입 변경
        type: "default" | "add" | "edit";
      }
    >
  >(new Map());

  // 도면에서 로컬로 제거된 카메라 ID (서버 데이터 갱신 전까지 즉시 숨김 처리)
  const [localRemovedCameraIds, setLocalRemovedCameraIds] = useState<Set<number>>(new Set());

  // 되돌릴 대상. 여기 빠진 상태를 바꾸는 동작은 pushUndo 를 불러도 화면이 그대로라
  // "Ctrl+Z 를 눌렀는데 아무 일도 안 일어나고 스택만 소모되는" 증상이 된다 (개선 니즈 #3).
  // 제외: 아이콘/라벨 크기(즉시 서버 저장), 카메라 메모(즉시 저장 — 화면만 되돌리면 서버와 어긋남),
  //       도면 메모 스타일(숫자 입력 onChange 라 타이핑 단위로 쪼개짐)
  const undoStack = useUndoStack(
    useMemo(() => ({
      localMappings: setLocalMappings,
      localRemovedCameraIds: setLocalRemovedCameraIds,
      tempCameras: setTempCameras,
      pendingAdjustments: setPendingAdjustments,
      pendingDiagramChanges: setPendingDiagramChanges,
      pendingTextChanges: setPendingTextChanges,
    }), []),
  );
  const localMappingsRef = useRef(localMappings);
  localMappingsRef.current = localMappings;
  const localRemovedCameraIdsRef = useRef(localRemovedCameraIds);
  localRemovedCameraIdsRef.current = localRemovedCameraIds;
  const tempCamerasRef = useRef(tempCameras);
  tempCamerasRef.current = tempCameras;
  const pendingAdjustmentsRef = useRef(pendingAdjustments);
  pendingAdjustmentsRef.current = pendingAdjustments;
  const pendingDiagramChangesRef = useRef(pendingDiagramChanges);
  pendingDiagramChangesRef.current = pendingDiagramChanges;
  const pendingTextChangesRef = useRef(pendingTextChanges);
  pendingTextChangesRef.current = pendingTextChanges;
  const pushUndo = useCallback(() => {
    undoStack.push({
      localMappings: localMappingsRef.current,
      localRemovedCameraIds: localRemovedCameraIdsRef.current,
      tempCameras: tempCamerasRef.current,
      pendingAdjustments: pendingAdjustmentsRef.current,
      pendingDiagramChanges: pendingDiagramChangesRef.current,
      pendingTextChanges: pendingTextChangesRef.current,
    });
  }, [undoStack]);
  // 위쪽(도면 메모 핸들러)에서 부를 수 있도록 연결
  pushUndoRef.current = pushUndo;

  // serverCameras가 갱신되면 이미 서버에 반영된 로컬 상태를 자동 정리
  useEffect(() => {
    // localRemovedCameraIds 정리: 서버에서 매핑 해제 확인되면 제거
    if (localRemovedCameraIds.size > 0) {
      setLocalRemovedCameraIds((prev) => {
        const next = new Set<number>();
        for (const id of prev) {
          const cam = serverCameras.find((c) => c.id === id);
          if (cam && cam.floorPlanId === selectedFloorPlanId) {
            next.add(id); // 아직 서버에 반영 안 됨 → 유지
          }
        }
        if (next.size === prev.size) return prev;
        return next;
      });
    }

    // localMappings 정리: 서버 데이터가 로컬 매핑(도면 + 위치)과 일치하면 제거
    if (localMappings.size > 0) {
      setLocalMappings((prev) => {
        const next = new Map(prev);
        let changed = false;
        for (const [cameraId, mapping] of prev) {
          const cam = serverCameras.find((c) => c.id === cameraId);
          if (!cam) continue;
          // 도면 ID + 위치까지 모두 일치해야 서버에 반영된 것으로 판단
          const floorPlanMatch = cam.floorPlanId === mapping.floorPlanId;
          const positionMatch = cam.position
            && Math.abs(cam.position.x - mapping.position.x) < 0.01
            && Math.abs(cam.position.y - mapping.position.y) < 0.01
            && Math.abs(cam.position.rotation - mapping.position.rotation) < 0.01;
          if (floorPlanMatch && positionMatch) {
            next.delete(cameraId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    }
  }, [serverCameras, selectedFloorPlanId, localRemovedCameraIds.size, localMappings.size]);

  // 버전 변경 시 자동 선택 리셋
  const hasAutoSelectedRef = useRef(false);
  useEffect(() => {
    hasAutoSelectedRef.current = false;
    setSelectedFloorPlanId(null);
  }, [versionId]);

  // 도면/카메라 자동 선택 — per-branch 저장된 마지막 선택을 우선 복원, 없으면 derived default
  // (h ①: 페이지 이탈 후 돌아왔을 때 직전 작업 컨텍스트 유지)
  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (isCamerasPending || isFloorPlansPending) return;
    if (floorPlans.length === 0) return;
    if (!branchUid) return;

    // 사이드바 정렬과 동일 — 분석번호 우선(지하 b3→b1 → 지상 1→n), N-prefix 카메라는 뒤, 미부여는 맨 뒤
    const pickDefaultCamera = () => {
      if (cameras.length === 0) return null;
      const sorted = [...cameras].sort((a, b) => {
        const aNum = a.analysisNumber ?? "";
        const bNum = b.analysisNumber ?? "";
        if (!aNum && !bNum) return 0;
        if (!aNum) return 1;
        if (!bNum) return -1;
        return compareCamId(aNum, bNum);
      });
      return sorted[0];
    };

    // 1) 저장된 선택 복원 — 현재 도면/카메라 목록에서 유효한 경우만
    const remembered = readMappingSelection(branchUid);
    const rememberedFloorPlanValid =
      remembered?.floorPlanId != null &&
      floorPlans.some((fp) => fp.id === remembered.floorPlanId);
    const rememberedCameraValid =
      remembered?.cameraId != null &&
      cameras.some((c) => c.id === remembered.cameraId);

    if (rememberedFloorPlanValid) {
      setSelectedFloorPlanId(remembered!.floorPlanId!);
      if (rememberedCameraValid) {
        setSelectedCameraId(remembered!.cameraId!);
      } else {
        // 도면은 복원, 카메라는 기억 못 함 → derived default 카메라
        const fallback = pickDefaultCamera();
        setSelectedCameraId(fallback?.id ?? null);
      }
      hasAutoSelectedRef.current = true;
      return;
    }

    // 2) 저장된 선택 없음 → 기존 derived default
    const selectedCamera = pickDefaultCamera();
    if (selectedCamera) {
      setSelectedCameraId(selectedCamera.id);
      const cameraFloorPlanId = selectedCamera.floorPlanId;
      const chosenFloorPlanId =
        cameraFloorPlanId != null && floorPlans.some((fp) => fp.id === cameraFloorPlanId)
          ? cameraFloorPlanId
          : floorPlans[0].id;
      setSelectedFloorPlanId(chosenFloorPlanId);
    } else {
      setSelectedFloorPlanId(floorPlans[0].id);
    }

    hasAutoSelectedRef.current = true;
  }, [cameras, floorPlans, isCamerasPending, isFloorPlansPending, branchUid]);

  // 선택 변경 시 per-branch 저장 (h ①)
  useEffect(() => {
    if (!branchUid) return;
    if (!hasAutoSelectedRef.current) return; // 초기 auto-select 진행 중인 케이스는 스킵
    writeMappingSelection(branchUid, {
      floorPlanId: selectedFloorPlanId,
      cameraId: selectedCameraId,
    });
  }, [branchUid, selectedFloorPlanId, selectedCameraId]);

  // 현재 선택된 도면에 매핑된 카메라들 (서버 데이터 + 로컬 매핑 - 로컬 삭제)
  const camerasOnFloorPlan = useMemo(() => {
    const fromServer = cameras.filter(
      (c) => c.floorPlanId === selectedFloorPlanId && !localRemovedCameraIds.has(c.id),
    );
    const fromLocal = cameras.filter((c) => {
      const mapping = localMappings.get(c.id);
      return (
        mapping &&
        mapping.floorPlanId === selectedFloorPlanId &&
        c.floorPlanId !== selectedFloorPlanId
      );
    });
    return [...fromServer, ...fromLocal];
  }, [cameras, selectedFloorPlanId, localMappings, localRemovedCameraIds]);

  // F9: 목록 필터에서 탈락한 카메라는 도면에서도 숨김 (레거시 동작). null 이면 필터 미적용 → 전체 표시.
  const visibleCamerasOnFloorPlan = useMemo(
    () => (visibleCameraIds ? camerasOnFloorPlan.filter((c) => visibleCameraIds.has(c.id)) : camerasOnFloorPlan),
    [camerasOnFloorPlan, visibleCameraIds],
  );

  // 어느 도면에든 매핑된 카메라 (서버 매핑 + 로컬 매핑 - 로컬 제거)
  const someFloorPlanCameraIds = useMemo(() => {
    const set = new Set<number>();
    for (const c of cameras) {
      if (c.floorPlanId !== null) set.add(c.id);
    }
    for (const [id, mapping] of localMappings) {
      if (mapping.floorPlanId !== null) set.add(id);
    }
    for (const id of localRemovedCameraIds) {
      // 로컬 제거 대기 + 다른 로컬 매핑(다른 도면에)이 없으면 미매핑으로 취급
      const stillLocallyMapped = localMappings.has(id);
      if (!stillLocallyMapped) set.delete(id);
    }
    return set;
  }, [cameras, localMappings, localRemovedCameraIds]);

  // 현재 도면의 카메라 위치 맵 (로컬 매핑에서 추출)
  const cameraPositions = useMemo(() => {
    const positions = new Map<
      number,
      { x: number; y: number; rotation: number; type: "default" | "add" | "edit" }
    >();
    for (const [cameraId, mapping] of localMappings) {
      if (mapping.floorPlanId === selectedFloorPlanId) {
        positions.set(cameraId, {
          x: mapping.position.x,
          y: mapping.position.y,
          rotation: mapping.position.rotation,
          type: mapping.type,
        });
      }
    }
    return positions;
  }, [localMappings, selectedFloorPlanId]);

  // 카메라 매핑 타입 맵 (서버 mappingType + 로컬 매핑 통합)
  const cameraMappingTypes = useMemo(() => {
    const types = new Map<number, "add" | "edit">();
    // 서버에서 가져온 mappingType 반영 (임시 카메라만 뱃지 표시)
    for (const camera of cameras) {
      if (camera.mappingType === "add") {
        types.set(camera.id, camera.mappingType);
      }
    }
    // 로컬 매핑: 임시 카메라(add)만 뱃지 표시
    for (const [cameraId, mapping] of localMappings) {
      if (mapping.type === "add") {
        types.set(cameraId, mapping.type);
      }
    }
    return types;
  }, [cameras, localMappings]);

  // 미분석 카메라 넘버링 — 서버에 저장된 N번호는 그대로, null인 카메라(임시 등)만 새 번호 부여
  // (4-5-3) 빈 번호 채우기 X — 현재 max + 1 부터 뒤로 쌓는다
  const nonAnalysisNumberMap = useMemo(() => {
    const map = new Map<number, string>();
    const usedNNumbers = new Set<string>();
    let maxN = 0;
    // 서버에서 이미 N번호가 저장된 카메라는 그대로 사용
    for (const cam of cameras) {
      if (cam.analysisNumber != null && cam.analysisNumber.startsWith("N")) {
        map.set(cam.id, cam.analysisNumber);
        usedNNumbers.add(cam.analysisNumber);
        const v = parseInt(cam.analysisNumber.slice(1), 10);
        if (Number.isFinite(v) && v > maxN) maxN = v;
      }
    }
    // analysisNumber가 null인 카메라(임시 등)에만 새 N번호 부여
    const nullCams = cameras.filter((c) => c.analysisNumber === null);
    for (const cam of nullCams) {
      let n = maxN + 1;
      while (usedNNumbers.has(`N${n}`)) n++;
      const num = `N${n}`;
      map.set(cam.id, num);
      usedNNumbers.add(num);
      maxN = n;
    }
    return map;
  }, [cameras]);

  const handleCameraClick = useCallback((camera: Camera) => {
    const isDeselecting = camera.id === selectedCameraId;
    setSelectedCameraId((prev) => (prev === camera.id ? null : camera.id));
    setSelectedMemoId(null);
    if (!isDeselecting) {
      const local = localMappings.get(camera.id);
      const fpId = local?.floorPlanId ?? camera.floorPlanId;
      if (fpId != null && fpId !== selectedFloorPlanId) {
        setSelectedFloorPlanId(fpId);
        setMemoPlacingMode(null);
      }
    }
  }, [selectedCameraId, selectedFloorPlanId, localMappings]);

  // 캔버스 마커 클릭은 토글 금지 — 선택된 카메라를 다시 클릭(=드래그 시작)할 때
  // null 로 토글되면 selection sync 가 activeObject 를 discard 해서 드래그가 풀린다.
  const handleCameraMarkerClick = useCallback((camera: Camera) => {
    setSelectedCameraId(camera.id);
    setSelectedMemoId(null);
  }, []);

  const handleCameraSelect = useCallback((cameraId: number) => {
    setSelectedCameraId(cameraId);
    setSelectedMemoId(null);
  }, []);

  const applyCameraPlacement = useCallback(
    (cameraId: number, position: { x: number; y: number; rotation: number }) => {
      if (selectedFloorPlanId == null) return;
      pushUndo();
      setLocalMappings((prev) => {
        const next = new Map(prev);
        const existing = prev.get(cameraId);
        const camera = cameras.find((c) => c.id === cameraId);
        // 단순 이동은 type 을 변경하지 않는다(default 로 마킹) — 서버 type 보존.
        // "add" 는 임시 카메라 매핑 케이스(서버에 add 로 저장된 카메라)에서만 유지.
        // "edit" 은 화각 조정 같은 별도 액션에서 명시적으로 설정 (3-6-x).
        const type: "default" | "add" | "edit" = existing
          ? existing.type
          : camera?.mappingType === "add"
            ? "add"
            : "default";
        next.set(cameraId, { floorPlanId: selectedFloorPlanId, position, type });
        return next;
      });
      // 같은 카메라가 로컬에서 제거 대기 중이었다면 재배치로 취소
      setLocalRemovedCameraIds((prev) => {
        if (!prev.has(cameraId)) return prev;
        const next = new Set(prev);
        next.delete(cameraId);
        return next;
      });
    },
    [selectedFloorPlanId, cameras],
  );

  // 선택된 카메라가 어디에도 매핑되지 않은 신규 상태인지.
  // - server 매핑 없음 + localMappings 없음 + (localRemovedCameraIds 에 있어 매핑 해제 대기 중) 도 미배치로 간주
  // 이 값이 true 일 때만 도면 클릭이 placement 동작 — 다른 도면에 매핑된 카메라 선택 후 빈 영역
  // 클릭에서 placement conflict 모달이 뜨던 회귀 방지 (PO QA i 보강)
  const selectedCameraIsUnplaced = useMemo(() => {
    if (selectedCameraId == null) return false;
    const localMapping = localMappings.get(selectedCameraId);
    if (localMapping?.floorPlanId != null) return false;
    if (localRemovedCameraIds.has(selectedCameraId)) return true;
    const camera = cameras.find((c) => c.id === selectedCameraId);
    return camera?.floorPlanId == null;
  }, [selectedCameraId, localMappings, localRemovedCameraIds, cameras]);

  // ESC 키: 카메라/메모 선택 해제
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if (target?.isContentEditable) return;
      if (selectedCameraId != null || selectedMemoId != null) {
        setSelectedCameraId(null);
        setSelectedMemoId(null);
        setMemoPlacingMode(null);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [selectedCameraId, selectedMemoId]);

  const handleCameraPlacedOnFloorPlan = useCallback(
    (cameraId: number, position: { x: number; y: number; rotation: number }) => {
      if (selectedFloorPlanId == null) return;
      // 충돌 감지 — 로컬 매핑 우선, 없으면 서버 매핑. 단 로컬 제거 대기 중이면 무시.
      const localMapping = localMappings.get(cameraId);
      const camera = cameras.find((c) => c.id === cameraId);
      const isLocallyRemoved = localRemovedCameraIds.has(cameraId);
      const existingFloorPlanId = isLocallyRemoved
        ? null
        : (localMapping?.floorPlanId ?? camera?.floorPlanId ?? null);
      if (
        existingFloorPlanId != null
        && existingFloorPlanId !== selectedFloorPlanId
      ) {
        // 다른 도면에 이미 매핑됨 — 사용자에게 옮길지 확인
        setPlacementConflict({ cameraId, position, fromFloorPlanId: existingFloorPlanId });
        return;
      }
      applyCameraPlacement(cameraId, position);
    },
    [selectedFloorPlanId, localMappings, cameras, localRemovedCameraIds, applyCameraPlacement],
  );

  const handleConfirmMoveCameraPlacement = useCallback(() => {
    if (!placementConflict) return;
    applyCameraPlacement(placementConflict.cameraId, placementConflict.position);
    setPlacementConflict(null);
  }, [placementConflict, applyCameraPlacement]);

  const handleCancelMoveCameraPlacement = useCallback(() => {
    setPlacementConflict(null);
  }, []);

  // 화각 조정 시작 — 카메라 현재 위치를 복사해 조정 상태를 만든다.
  // 사용자는 캔버스에서 메인 마커를 드래그/회전해 조정본 위치를 변경.
  // 조정 모드 진입 — pending 엔트리 없으면 adjustmentPosition / localMapping / camera.position 순으로 초기화
  // (신규 배치 카메라는 서버 position 이 null 이고 localMapping 에만 위치가 있으므로 같이 본다)
  const handleStartAdjustment = useCallback((cameraId: number) => {
    const camera = cameras.find((c) => c.id === cameraId);
    if (!camera) return;
    // PO QA 4차 af: UI disabled 우회 방어 — 액션 핸들러에서도 스냅샷 존재 verify
    if (!hasRealSnapshot(camera)) return;
    const localMapping = localMappings.get(cameraId);
    // j-3: 조정 취소 후 재진입 시 fresh — pending(null) 은 "wipe 대기" 마커. 이 상태에서 재진입하면
    // 서버 adjustment 데이터(adjustmentPosition/Frame)는 무시하고 현재 placement 위치 기준으로 초기화.
    const hasWipeMarker = pendingAdjustments.has(cameraId) && pendingAdjustments.get(cameraId) === null;
    const fallbackPosition = hasWipeMarker
      ? localMapping?.position ?? camera.position
      : camera.adjustmentPosition ?? localMapping?.position ?? camera.position;
    if (!fallbackPosition) return;
    pushUndo();
    setPendingAdjustments((prev) => {
      const existing = prev.get(cameraId);
      const next = new Map(prev);
      if (existing) {
        // 이미 pending(객체)이면 모드만 진입 + committed 를 false 로 되돌림 (j-1: 재진입 = uncommitted)
        next.set(cameraId, { ...existing, committed: false });
        return next;
      }
      const existingId = camera.adjustmentId
        ?? String(Date.now()) + Math.floor(Math.random() * 1000);
      next.set(cameraId, {
        adjustmentId: existingId,
        position: {
          x: fallbackPosition.x,
          y: fallbackPosition.y,
          rotation: fallbackPosition.rotation,
        },
        // wipe 마커 이후 재진입은 frame 도 fresh
        frame: hasWipeMarker ? { dx: 0, dy: 0 } : (camera.adjustmentFrame ?? { dx: 0, dy: 0 }),
        committed: false,
      });
      return next;
    });
    setAdjustmentEditingCameraId(cameraId);
  }, [cameras, localMappings, pendingAdjustments, pushUndo]);

  // 조정 종료 (완료) — pending 엔트리를 committed=true 로 마킹. 저장 대상에 포함됨 (j-1 B안)
  const handleEndAdjustment = useCallback(() => {
    setPendingAdjustments((prev) => {
      const editingId = adjustmentEditingCameraId;
      if (editingId == null) return prev;
      const existing = prev.get(editingId);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(editingId, { ...existing, committed: true });
      return next;
    });
    setAdjustmentEditingCameraId(null);
  }, [adjustmentEditingCameraId]);

  // 조정 취소 — pending 제거. 서버에 저장된 게 있으면 null 로 표시(저장 시 wipe), 없으면 큐에서 빼기
  const handleCancelAdjustment = useCallback((cameraId: number) => {
    pushUndo();
    setPendingAdjustments((prev) => {
      const next = new Map(prev);
      const camera = cameras.find((c) => c.id === cameraId);
      if (camera?.adjustmentId != null) {
        next.set(cameraId, null);
      } else {
        next.delete(cameraId);
      }
      return next;
    });
    setAdjustmentEditingCameraId((prev) => (prev === cameraId ? null : prev));
  }, [cameras, pushUndo]);

  // 사용자가 캔버스에서 조정본 마커를 드래그/회전했을 때 위치 갱신
  // pending 엔트리가 없으면 (서버 adjustment 만 있던 카메라) 새로 만든다
  const handleAdjustmentMoved = useCallback(
    (cameraId: number, position: { x: number; y: number; rotation: number }) => {
      // object:modified 경로 — 드래그가 끝날 때 한 번
      pushUndoRef.current();
      setPendingAdjustments((prev) => {
        const next = new Map(prev);
        const existing = prev.get(cameraId);
        const camera = cameras.find((c) => c.id === cameraId);
        const adjustmentId =
          existing?.adjustmentId
          ?? camera?.adjustmentId
          ?? String(Date.now()) + Math.floor(Math.random() * 1000);
        const frame = existing?.frame ?? camera?.adjustmentFrame ?? { dx: 0, dy: 0 };
        // 6차 b: 조정 모드 진입 중인 카메라만 uncommitted (j-1 B안 유지). 그 외 (조정 모드 외부에서 main drag 한 "단순 이동") 는 committed=true → 저장 차단 안 함.
        // 조정 완료 안 한 draft 가 우발적으로 저장되는 걸 막는 게 j-1 의 원래 목적인데, 사용자가 조정 모드 자체에 진입한 적 없으면 그런 draft 도 아님.
        const isEditing = adjustmentEditingCameraId === cameraId;
        next.set(cameraId, { adjustmentId, position, frame, committed: !isEditing });
        return next;
      });
    },
    [cameras, adjustmentEditingCameraId],
  );

  // 사이드바 helper rect 드래그 → adjustment frame 갱신 (position 은 보존)
  const handleAdjustmentFrameChange = useCallback(
    (cameraId: number, frame: { dx: number; dy: number }) => {
      setPendingAdjustments((prev) => {
        const next = new Map(prev);
        const existing = prev.get(cameraId);
        const camera = cameras.find((c) => c.id === cameraId);
        if (existing) {
          // 드래그 중 = uncommitted
          next.set(cameraId, { ...existing, frame, committed: false });
          return next;
        }
        // pending 없으면 server 또는 camera.position 으로 초기화 후 frame 만 변경
        const startPosition = camera?.adjustmentPosition ?? camera?.position;
        if (!startPosition) return prev;
        const adjustmentId =
          camera?.adjustmentId
          ?? String(Date.now()) + Math.floor(Math.random() * 1000);
        next.set(cameraId, {
          adjustmentId,
          position: { ...startPosition },
          frame,
          committed: false,
        });
        return next;
      });
    },
    [cameras],
  );

  // 카메라가 "유효한 조정 상태" 인지 — pending(객체) 우선, 없으면 서버 값 사용. pending(null) 이면 제거 예정 → 없는 것으로 취급.
  // 서버 adjustment_status 가 존재하면 adjustment_id 가 빈 문자열이어도 조정으로 간주 (PATCH 시 unique 값으로 채움)
  const effectiveAdjustment = useCallback(
    (cameraId: number): AdjustmentInfo | null => {
      if (pendingAdjustments.has(cameraId)) {
        return pendingAdjustments.get(cameraId) ?? null;
      }
      const camera = cameras.find((c) => c.id === cameraId);
      if (!camera || !camera.adjustmentPosition) return null;
      // 서버 position 이 null 인 카메라(미매핑) 의 adjustment_status 는 orphan stale 데이터 — 활성으로 보지 않음.
      // (PO QA 3차 s: 최초 매핑 시 '조정' 라벨이 잘못 노출되던 버그. 삭제→재매핑 경로엔 wipe 마커가 들어가는데
      // 최초 매핑 경로엔 없어 server orphan 이 그대로 반영됐음)
      if (camera.position == null) return null;
      // 서버 저장본은 이미 committed
      return {
        adjustmentId: camera.adjustmentId ?? "",
        position: camera.adjustmentPosition,
        frame: camera.adjustmentFrame ?? { dx: 0, dy: 0 },
        committed: true,
      };
    },
    [pendingAdjustments, cameras],
  );

  // 캔버스 렌더링용: 활성 조정 상태가 있는 카메라들의 Map
  const effectiveAdjustments = useMemo(() => {
    const result = new Map<number, AdjustmentInfo>();
    for (const camera of cameras) {
      const adjusted = effectiveAdjustment(camera.id);
      if (adjusted) result.set(camera.id, adjusted);
    }
    return result;
  }, [cameras, effectiveAdjustment]);

  const handleAddTempCamera = useCallback(() => {
    pushUndo();
    tempCameraSeq -= 1;
    const tempCamera: Camera = {
      id: tempCameraSeq,
      pseudoCameraId: tempCameraSeq,
      defaultCamId: `TEMP_${Math.abs(tempCameraSeq)}`,
      branchUid: branchUid!,
      previewImageUrl: TEMP_IMAGE_URL,
      analysisNumber: null,
      floorPlanId: null,
      position: null,
      mappingType: "add",
      additionId: null,
      adjustmentId: null,
      adjustmentPosition: null,
      adjustmentFrame: null,
    };
    setTempCameras((prev) => [...prev, tempCamera]);
    setSelectedCameraId(tempCamera.id);
    setSelectedMemoId(null);
  }, [branchUid]);

  // 메모는 전체 저장과 분리되어 동작 즉시 서버에 반영된다.
  // 화면은 낙관적 업데이트로 먼저 바뀌고, 실패하면 되돌린 뒤 토스트로 알린다.
  //
  // 예외: 아직 저장 안 된 신규 카메라(임시 음수 id)에 단 메모는 서버가 받을 cameraId 가 없다.
  // 이 메모만 큐에 담아 두고 카메라 저장 시점에 실제 id 로 바꿔 함께 보낸다.
  const handleMemoSave = useCallback((actions: MemoAction[]) => {
    if (actions.length === 0) return;

    const deferred: MemoAction[] = [];
    const immediate: MemoAction[] = [];
    for (const action of actions) {
      if (action.data.cameraId < 0) {
        deferred.push(
          action.actionType === "create"
            // _tempId 는 squashMemoActions 가 create/update 를 합칠 때만 쓰는 내부 표식이라
            // 서버로 나가기 전에 제거된다 (squashMemoActions 참고)
            ? ({ ...action, data: { ...action.data, _tempId: --tempMemoSeq } } as MemoAction)
            : action,
        );
      } else {
        immediate.push(action);
      }
    }

    if (deferred.length > 0) {
      setPendingTempCameraMemoActions((prev) => [...prev, ...deferred]);
    }
    if (immediate.length > 0) {
      updateCameraMemos.mutate(immediate, {
        onError: () => {
          showToast({
            type: "error",
            title: t("toast.memoSaveError.title"),
            content: t("toast.memoSaveError.content"),
          });
        },
      });
    }
  }, [updateCameraMemos, showToast, t]);

  const handleFloorPlanChange = useCallback((floorPlanId: number) => {
    setSelectedFloorPlanId(floorPlanId);
    setMemoPlacingMode(null);
  }, []);

  // 현재 선택된 도면에 매핑된 메모들
  const memosOnFloorPlan = useMemo(
    () => effectiveMemos.filter((m) => m.floorPlanId === selectedFloorPlanId),
    [effectiveMemos, selectedFloorPlanId],
  );

  const [pendingMemoRemoval, setPendingMemoRemoval] = useState<CameraMemo | null>(null);

  const handleToggleMemoOnFloorPlan = useCallback((memo: CameraMemo) => {
    const isPlaced =
      memo.floorPlanId === selectedFloorPlanId
      && selectedFloorPlanId != null
      && memo.positionForFloorPlan != null;
    if (isPlaced) {
      // 도면 위 마커 삭제와 동일한 확인 다이얼로그를 통해 일관된 UX 제공
      setPendingMemoRemoval(memo);
    } else {
      // 배치 모드 진입
      setMemoPlacingMode({ memoId: memo.id, cameraId: memo.cameraId });
    }
  }, [selectedFloorPlanId]);

  const handleConfirmMemoRemoval = useCallback(() => {
    if (!pendingMemoRemoval) return;
    handleMemoSave([
      { actionType: "update", data: { ...pendingMemoRemoval, floorPlanId: null, positionForFloorPlan: null } },
    ]);
    setPendingMemoRemoval(null);
  }, [pendingMemoRemoval, handleMemoSave]);

  const handleMemoPlaced = useCallback((memoId: number, position: { x: number; y: number }) => {
    if (selectedFloorPlanId == null) return;
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;

    handleMemoSave([
      { actionType: "update", data: { ...memo, floorPlanId: selectedFloorPlanId, positionForFloorPlan: position } },
    ]);
    setMemoPlacingMode(null);
  }, [selectedFloorPlanId, effectiveMemos, handleMemoSave]);

  const handleMemoMarkerClick = useCallback((memoId: number | null) => {
    if (memoId == null) {
      setSelectedMemoId(null);
      return;
    }
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (memo) {
      // 사이드바 메모 패널이 카메라 컨텍스트에서 메모 리스트를 보여주므로
      // 메모 클릭 시 소속 카메라도 selectedCameraId 로 세팅 필요.
      // 다만 동시에 카메라 marker toolbar 가 뜨면 안 되므로, FloorPlanCanvas 측에서
      // selectedMemoId 가 set 되어 있으면 카메라 toolbar 노출을 억제한다.
      setSelectedCameraId(memo.cameraId);
      setSelectedMemoId(memoId);
    }
  }, [effectiveMemos]);

  const handleCanvasSelectionCleared = useCallback(() => {
    setSelectedMemoId(null);
  }, []);

  const handleMemoDragEnd = useCallback((memoId: number, position: { x: number; y: number }) => {
    if (selectedFloorPlanId == null) return;
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;

    handleMemoSave([
      { actionType: "update", data: { ...memo, floorPlanId: selectedFloorPlanId, positionForFloorPlan: position } },
    ]);
  }, [selectedFloorPlanId, effectiveMemos, handleMemoSave]);

  // 도면에서 카메라 매핑 제거 (로컬에서 즉시 숨기고, 저장 시 서버에 반영)
  const handleDeleteCameraFromFloorPlan = useCallback((cameraId: number) => {
    pushUndo();
    // 로컬 매핑이 있으면 제거
    setLocalMappings((prev) => {
      const next = new Map(prev);
      next.delete(cameraId);
      return next;
    });

    // 화면에서 즉시 숨김 + 저장 시 서버에 반영할 삭제 목록에 추가
    setLocalRemovedCameraIds((prev) => new Set(prev).add(cameraId));

    // j-2: 도면 제거 후 재추가 시 fresh — adjustment 데이터 wipe.
    // 서버 저장 본이 있으면 null 마커(저장 시 wipe), 로컬 pending 만 있으면 큐에서 제거.
    const camera = cameras.find((c) => c.id === cameraId);
    setPendingAdjustments((prev) => {
      if (camera?.adjustmentId != null) {
        const next = new Map(prev);
        next.set(cameraId, null);
        return next;
      }
      if (!prev.has(cameraId)) return prev;
      const next = new Map(prev);
      next.delete(cameraId);
      return next;
    });
    setAdjustmentEditingCameraId((prev) => (prev === cameraId ? null : prev));

    if (selectedCameraId === cameraId) {
      setSelectedCameraId(null);
    }
  }, [cameras, selectedCameraId]);

  // 도면에서 메모 제거
  const handleDeleteMemoFromFloorPlan = useCallback((memoId: number) => {
    const memo = effectiveMemos.find((m) => m.id === memoId);
    if (!memo) return;
    handleMemoSave([
      { actionType: "update", data: { ...memo, floorPlanId: null, positionForFloorPlan: null } },
    ]);
  }, [effectiveMemos, handleMemoSave]);

  const [isSaving, setIsSaving] = useState(false);

  const hasUnsavedChanges = localMappings.size > 0
    || localRemovedCameraIds.size > 0
    || tempCameras.length > 0
    || pendingTempCameraMemoActions.length > 0
    || pendingDiagramChanges.size > 0
    || pendingTextChanges.size > 0
    || pendingAdjustments.size > 0;
  // 아이콘/라벨 크기는 슬라이더에서 손 뗄 때 즉시 저장 → 미저장 변경(이탈 가드) 대상 아님

  // 저장 후 분석 영역 설정: 저장 직후 navigate 시점엔 hasUnsavedChanges 가 아직 리렌더로
  // false 가 안 됐을 수 있어 가드가 잘못 발동 → 이 ref 로 동기 우회.
  const navigatingAfterSaveRef = useRef(false);
  useUnsavedChangesGuard(hasUnsavedChanges, tShared("unsavedChangesGuard.message"), navigatingAfterSaveRef);

  const saveMappings = useCallback(async (): Promise<SaveResult> => {
    if (!hasUnsavedChanges) return { failed: 0, total: 0 };
    setIsSaving(true);
    try {
      // 1) 임시 카메라 생성 (add-mode: preview 업로드 X, extra_id 만 전달) + N번호 부여
      //    PO QA 5차 af/ah 최종: temp_image.png 업로드로 preview_img_url 이 가짜 S3 URL 로 박히던 root cause 제거.
      //    레거시 빠른 추가와 동일하게 BE 가 preview_img_url=null 로 저장 → hasRealSnapshot 첫 줄에서 자연 차단.
      const usedNNumbers = new Set<string>();
      const usedExtraIds = new Set<string>();
      for (const cam of serverCameras) {
        if (cam.analysisNumber != null && cam.analysisNumber.startsWith("N")) {
          usedNNumbers.add(cam.analysisNumber);
        }
        if (cam.additionId != null) {
          usedExtraIds.add(cam.additionId);
        }
      }
      const tempIdSet = new Set(tempCameras.map((c) => c.id));
      const tempIdToRealId = new Map<number, number>();
      let nextExtraSeq = 1;
      for (const temp of tempCameras) {
        // N 번호를 createCamera 호출 **전에** 결정 + 로컬 tempCamera 에 박아둠.
        // createCamera 의 onSuccess invalidate 가 트리거하는 refetch 가 BE 의 새 server camera
        // (analysisNumber=null) 를 가져오면, 로컬 temp 가 null 인 시점엔 nonAnalysisNumberMap 이
        // 둘 다 nullCam 으로 보고 N3/N4 식으로 재할당 → temp 가 N4 로 잠깐 튐. 로컬에선 BE latency 가
        // 너무 짧아 race window 가 안 생기지만 베타에선 보임. temp 의 N 번호를 미리 박아 race 제거.
        let n = 1;
        while (usedNNumbers.has(`N${n}`)) n++;
        const num = `N${n}`;
        usedNNumbers.add(num);
        setTempCameras((prev) => prev.map((t) => (t.id === temp.id ? { ...t, analysisNumber: num } : t)));

        while (usedExtraIds.has(`${nextExtraSeq}`)) nextExtraSeq++;
        const extraId = `${nextExtraSeq}`;
        usedExtraIds.add(extraId);
        const result = await createCamera.mutateAsync({ extraId });
        const orientationId = result?.pseudo_camera_orientation_id;
        if (orientationId) {
          tempIdToRealId.set(temp.id, orientationId);
          // 저장 후 selectedCameraId 가 tempId 인 상태로 남으면 cameras.find 가 undefined →
          // selectedCameraIsUnplaced 가 true 로 평가되어 placement 커서 모드가 잘못 발동.
          setSelectedCameraId((current) => (current === temp.id ? orientationId : current));
          await updateAnalysisNumber.mutateAsync({ cameraOrientationId: orientationId, analysisNumber: num });
        }
      }
      // setTempCameras([]) 는 refetch 후로 미룸 — mapping PATCH + refetch 사이 cameras 리스트가
      // 비어 보이는 깜빡임 방지. tempCameras 가 살아있는 동안 fromLocal 가드(localMappings) 가 카메라
      // 렌더를 유지하다가, refetch + localMappings 정리 + tempCameras 정리가 React 18 auto-batching
      // 으로 한 render 에 일괄 적용되어 빈 프레임이 발생하지 않음.

      const resolveCameraId = (cameraId: number) => tempIdToRealId.get(cameraId) ?? cameraId;

      // 2) 카메라 매핑 저장 — 임시 ID는 실제 orientationId로 변환해 호출
      // 기존 add/edit 매핑은 서버 additionId/adjustmentId 를 보존, 신규는 update-camera-mapping 에서 unique 생성
      const mappingEntries = Array.from(localMappings.entries());
      console.log("[saveMappings] 매핑 저장:", mappingEntries.length, "건");
      const cameraMappingPromises = mappingEntries.map(
        ([cameraId, mapping]) => {
          const camera = cameras.find((c) => c.id === cameraId);
          return updateCameraMapping.mutateAsync({
            cameraOrientationId: resolveCameraId(cameraId),
            floorPlanId: mapping.floorPlanId,
            position: mapping.position,
            mappingType: mapping.type,
            additionId: mapping.type === "add" ? camera?.additionId ?? null : null,
            adjustmentId: mapping.type === "edit" ? camera?.adjustmentId ?? null : null,
          });
        },
      );

      // 3) 도면에서 제거된 카메라 매핑 해제 — 서버에 존재하지 않는 임시 ID는 스킵
      // 서버는 N-prefix 분석번호(자동 할당 라벨)는 매핑 해제 허용 (clt-backend serializers.py 참고)
      const removedIds = Array.from(localRemovedCameraIds).filter((id) => !tempIdSet.has(id));
      console.log("[saveMappings] 삭제 저장:", removedIds.length, "건, IDs:", removedIds);
      const cameraDeletePromises = removedIds.map((cameraId) =>
        updateCameraMapping.mutateAsync({
          cameraOrientationId: cameraId,
          floorPlanId: null,
          position: null,
          mappingType: "default",
        }),
      );

      // 3.5) 화각 조정 저장 — pending(객체)는 set, pending(null)은 wipe.
      // j-1 B안: committed=false 인 항목(조정 완료 안 한 draft)은 저장 대상에서 제외 — 로컬에 그대로 유지.
      // 임시 카메라(아직 서버에 없음)는 매핑 저장 후 별도로 처리해야 하지만, 임시 카메라에 대한 조정은 일반적이지 않으므로 일단 스킵
      const adjustmentPromises = Array.from(pendingAdjustments.entries())
        .filter(([cameraId]) => !tempIdSet.has(cameraId))
        .filter(([_, info]) => info === null || info.committed)
        .map(([cameraId, info]) =>
          updateCameraAdjustment.mutateAsync({
            cameraOrientationId: cameraId,
            adjustment: info,
          }),
        );

      // 4) (아이콘/라벨 크기는 슬라이더 조작 시 즉시 저장 — 여기서 저장하지 않음)

      // 5) 신규 카메라에 단 메모 저장
      // 일반 메모는 이미 동작 시점에 저장됐다. 여기 남는 건 임시 카메라 id 때문에 보낼 수
      // 없었던 메모뿐이라, 이제 실제 orientationId 로 바꿔 보낸다.
      // (서버는 pseudo_camera_orientation_id 가 음수면 유효하지 않은 pk 로 거부한다)
      //
      // 메모 저장은 다른 저장과 함께 allSettled 배치로 넘긴다(await 하지 않는다).
      // 예전엔 여기서 바로 await 해서, 메모 저장이 한 번 실패하면 뒤따르는 도면 메모 저장이
      // 아예 전송되지 않았다 (ctos: mST 메모 삭제 오류).
      let memoSavePromise: Promise<unknown> | null = null;
      if (pendingTempCameraMemoActions.length > 0) {
        const resolved = squashMemoActions(pendingTempCameraMemoActions).map((action) => ({
          ...action,
          data: { ...action.data, cameraId: resolveCameraId(action.data.cameraId) },
        })) as MemoAction[];
        if (resolved.length > 0) {
          // 성공했을 때만 비운다 — 실패하면 사용자가 쓴 내용을 잃지 않고 재시도할 수 있어야 한다
          memoSavePromise = updateCameraMemos
            .mutateAsync(resolved)
            .then(() => setPendingTempCameraMemoActions([]));
        } else {
          setPendingTempCameraMemoActions([]);
        }
      }

      // 6) 도면 메모 변경사항 저장 (생성/편집/삭제)
      console.log("[saveMappings] pending diagrams:", Array.from(pendingDiagramChanges.entries()));
      console.log("[saveMappings] pending texts:", Array.from(pendingTextChanges.entries()));
      const diagramPromises: Promise<unknown>[] = [];
      for (const [memoId, change] of pendingDiagramChanges) {
        if (change.type === "create") {
          console.log("[saveMappings] creating diagram", { floorPlanId: change.floorPlanId, coords: change.coordinates, style: change.style });
          diagramPromises.push(
            createFloorPlanMemoDiagram({
              floorPlanId: change.floorPlanId,
              coordinates: change.coordinates,
              style: change.style,
            }).catch((err) => { console.error("[saveMappings] diagram create failed:", err?.response?.data || err); throw err; }),
          );
        } else if (change.type === "delete") {
          if (memoId < 0) continue; // 임시 ID는 서버에 없으니 스킵
          diagramPromises.push(
            deleteFloorPlanMemoDiagram({ floorPlanId: change.floorPlanId, memoDiagramId: memoId }),
          );
        } else {
          diagramPromises.push(
            updateFloorPlanMemoDiagram({
              floorPlanId: change.floorPlanId,
              memoDiagramId: memoId,
              coordinates: change.coordinates,
              style: change.style,
            }),
          );
        }
      }
      const textPromises: Promise<unknown>[] = [];
      for (const [memoId, change] of pendingTextChanges) {
        if (change.type === "create") {
          console.log("[saveMappings] creating text", { floorPlanId: change.floorPlanId, position: change.position, content: change.content, style: change.style });
          textPromises.push(
            createFloorPlanMemoText({
              floorPlanId: change.floorPlanId,
              content: change.content,
              position: change.position,
              style: change.style,
            }).catch((err) => { console.error("[saveMappings] text create failed:", err?.response?.data || err); throw err; }),
          );
        } else if (change.type === "delete") {
          if (memoId < 0) continue;
          textPromises.push(
            deleteFloorPlanMemoText({ floorPlanId: change.floorPlanId, memoTextId: memoId }),
          );
        } else {
          textPromises.push(
            updateFloorPlanMemoText({
              floorPlanId: change.floorPlanId,
              memoTextId: memoId,
              content: change.content,
              position: change.position,
              style: change.style,
            }),
          );
        }
      }

      // 메모 저장은 맨 뒤에 붙인다 — 아래 settled 인덱스 계산이 앞 구간(매핑/삭제)에
      // 의존하므로 중간에 끼워 넣으면 어긋난다.
      const allBatchPromises = [
        ...cameraMappingPromises,
        ...cameraDeletePromises,
        ...adjustmentPromises,
        ...diagramPromises,
        ...textPromises,
        ...(memoSavePromise ? [memoSavePromise] : []),
      ];
      const settled = await Promise.allSettled(allBatchPromises);
      const batchFailed = settled.filter((r) => r.status === "rejected").length;
      const batchTotal = settled.length;
      let firstErrorMessage: string | undefined;
      for (const r of settled) {
        if (r.status === "rejected") {
          console.error("[saveMappings] batch item failed:", r.reason);
          // 서버가 알려준 실패 이유를 사용자에게 그대로 보여주기 위해 첫 건만 기록.
          // 없으면 "저장에 실패했습니다"만 뜨고 원인을 알 길이 없다.
          const data = r.reason?.response?.data;
          firstErrorMessage ??= typeof data?.message === "string"
            ? data.message
            : typeof data?.detail === "string"
              ? data.detail
              : undefined;
        }
      }

      // 저장 성공한 로컬 상태(localMappings/localRemovedCameraIds) 즉시 클리어.
      // cleanup effect 만 의존하면 saveMappings return 후에야 정리되어
      // 직후 페이지 이탈 시 unsaved guard 가 잘못 발동되는 케이스 발생 (PO QA g)
      const mappingCount = mappingEntries.length;
      const deleteCount = removedIds.length;
      const needsCameraRefetch =
        pendingAdjustments.size > 0 || mappingCount > 0 || deleteCount > 0;
      if (needsCameraRefetch) {
        await queryClient.refetchQueries({ queryKey: [CAMERAS_QUERY_KEY, branchUid, versionId] });
        if (pendingAdjustments.size > 0) {
          setPendingAdjustments(new Map());
        }
        if (mappingCount > 0) {
          const successfulMappingIds = new Set<number>();
          for (let i = 0; i < mappingCount; i++) {
            if (settled[i].status === "fulfilled") {
              successfulMappingIds.add(mappingEntries[i][0]);
            }
          }
          if (successfulMappingIds.size > 0) {
            setLocalMappings((prev) => {
              const next = new Map(prev);
              for (const id of successfulMappingIds) next.delete(id);
              return next;
            });
          }
        }
        // tempCameras 정리 — refetch + localMappings 정리와 같은 render 에 묶여 깜빡임 없이 전환.
        setTempCameras([]);
        if (deleteCount > 0) {
          const successfulDeleteIds = new Set<number>();
          for (let j = 0; j < deleteCount; j++) {
            const idx = mappingCount + j;
            if (settled[idx].status === "fulfilled") {
              successfulDeleteIds.add(removedIds[j]);
            }
          }
          if (successfulDeleteIds.size > 0) {
            setLocalRemovedCameraIds((prev) => {
              const next = new Set(prev);
              for (const id of successfulDeleteIds) next.delete(id);
              return next;
            });
          }
        }
      }

      // refetch를 await해서 fresh 서버 데이터 도착 후에 pending 클리어
      // (반대 순서면 1프레임 동안 stale 캐시 + 빈 pending이라 삭제된 메모가 잠깐 다시 보였다 사라지는 깜빡임)
      const refetchPromises: Promise<unknown>[] = [];
      if (pendingDiagramChanges.size > 0) {
        refetchPromises.push(
          queryClient.refetchQueries({
            queryKey: [FLOOR_PLAN_MEMO_DIAGRAMS_GROUPED_BY_FLOOR_PLAN_QUERY_KEY, branchUid, versionId],
          }),
        );
      }
      if (pendingTextChanges.size > 0) {
        refetchPromises.push(
          queryClient.refetchQueries({
            queryKey: [FLOOR_PLAN_MEMO_TEXTS_GROUPED_BY_FLOOR_PLAN_QUERY_KEY, branchUid, versionId],
          }),
        );
      }
      if (refetchPromises.length > 0) {
        await Promise.all(refetchPromises);
        setPendingDiagramChanges(new Map());
        setPendingTextChanges(new Map());
      }
      console.log("[saveMappings] 모든 저장 완료");
      // 임시 ID → 실제 ID 변환: cleanup effect가 서버 데이터와 매칭하도록
      // (변환 안 하면 음수 임시 키가 localMappings에 영구히 남고 화면 갱신 안 됨)
      if (tempIdToRealId.size > 0) {
        setLocalMappings((prev) => {
          const next = new Map(prev);
          for (const [tempId, realId] of tempIdToRealId) {
            const mapping = prev.get(tempId);
            if (mapping) {
              next.delete(tempId);
              next.set(realId, mapping);
            }
          }
          return next;
        });
        // 임시 카메라가 삭제 대기열에 있었다면 의미 없는 항목이므로 제거
        setLocalRemovedCameraIds((prev) => {
          let mutated = false;
          const next = new Set<number>();
          for (const id of prev) {
            if (tempIdSet.has(id)) {
              mutated = true;
              continue;
            }
            next.add(id);
          }
          return mutated ? next : prev;
        });
      }
      // localMappings, localRemovedCameraIds의 그 외 항목은 cleanup effect가
      // serverCameras refetch 후 자동 정리 (즉시 클리어하면 깜빡임 발생)
      return { failed: batchFailed, total: batchTotal, firstErrorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [
    hasUnsavedChanges,
    tempCameras,
    cameras,
    localMappings,
    localRemovedCameraIds,
    pendingTempCameraMemoActions,
    pendingDiagramChanges,
    pendingTextChanges,
    pendingAdjustments,
    serverCameras,
    createCamera,
    updateCameraMapping,
    updateCameraAdjustment,
    updateCameraMemos,
    updateAnalysisNumber,
    updateFloorPlanMemoDiagram,
    updateFloorPlanMemoText,
    deleteFloorPlanMemoDiagram,
    deleteFloorPlanMemoText,
    createFloorPlanMemoDiagram,
    createFloorPlanMemoText,
    queryClient,
    branchUid,
    versionId,
  ]);

  const handleSave = useCallback(async () => {
    try {
      const result = await saveMappings();
      if (result && result.failed > 0) {
        showToast({
          type: "error",
          title: t("toast.savePartial.title"),
          content: [
            t("toast.savePartial.content", { failed: result.failed, total: result.total }),
            result.firstErrorMessage,
          ].filter(Boolean).join("\n"),
        });
        return;
      }
      showToast({
        type: "success",
        title: t("toast.saveSuccess.title"),
        content: t("toast.saveSuccess.content"),
      });
    } catch (err) {
      console.error("[CameraMapping] save failed:", err);
      showToast({
        type: "error",
        title: t("toast.saveError.title"),
        content: t("toast.saveError.content"),
      });
    }
  }, [saveMappings, showToast, t]);

  const handleSaveAndAnalysis = useCallback(async () => {
    try {
      const result = await saveMappings();
      if (result && result.failed > 0) {
        // 부분 실패 시 분석 페이지로 이동하지 않음 — 사용자가 실패 항목 확인 후 재시도
        showToast({
          type: "error",
          title: t("toast.savePartial.title"),
          content: [
            t("toast.savePartial.content", { failed: result.failed, total: result.total }),
            result.firstErrorMessage,
          ].filter(Boolean).join("\n"),
        });
        return;
      }
      // 2026-06-11 duri 결정: 매핑완료(2) 단계 활성화 — "저장 후 분석 영역 설정" = CameraMapped(2) 로 transition (monotonic, 이미 더 높으면 유지).
      //   2→3(AnalysisDraft) 은 분석초안에서 라인/존을 그리면 effective status(getEffectiveStatus)가 데이터로 추론하므로 여기서 3 을 set하지 않는다.
      if (currentBranch && currentBranch.configStatus < BranchConfigStatus.CameraMapped) {
        try {
          await updateBranchConfig({
            branchUid: branchUid!,
            branchVersionId: versionId,
            config: { status: BranchConfigStatus.CameraMapped as unknown as LegacyBranchConfigStatus },
          });
          queryClient.invalidateQueries({ queryKey: [BRANCHES_QUERY_KEY] });
        } catch (statusErr) {
          console.error("[CameraMapping] configStatus update failed:", statusErr);
          // status 업데이트 실패해도 단계 완료의 사용자 흐름은 진행 (navigate)
        }
      }
      showToast({
        type: "success",
        title: t("toast.saveSuccess.title"),
        content: t("toast.saveSuccess.content"),
      });
      // 저장 성공 후 이동 — 가드 우회 (저장했으므로 이탈 경고 불필요)
      navigatingAfterSaveRef.current = true;
      navigate(`/branch/${branchUid}/analysis-draft`);
    } catch (err) {
      console.error("[CameraMapping] save failed:", err);
      showToast({
        type: "error",
        title: t("toast.saveError.title"),
        content: t("toast.saveError.content"),
      });
    }
  }, [saveMappings, showToast, navigate, branchUid, t, currentBranch, versionId, queryClient]);

  if (isCamerasPending || isFloorPlansPending) {
    return <Container><PageSpinner /></Container>;
  }

  return (
    <Container>
      <LeftPanel>
        <Header>
          <Title>{t("title")}</Title>
          <Label variant="soft" color="default" size="small">
            {cameras.length}
          </Label>
        </Header>
        <CameraSidebar
          cameras={cameras}
          cameraMappingTypes={cameraMappingTypes}
          configByCameraId={configByCameraId}
          onVisibleCameraIdsChange={setVisibleCameraIds}
          someFloorPlanCameraIds={someFloorPlanCameraIds}
          nonAnalysisNumberMap={nonAnalysisNumberMap}
          selectedCameraId={selectedCameraId}
          memos={effectiveMemos}
          selectedFloorPlanId={selectedFloorPlanId}
          selectedMemoId={selectedMemoId}
          floorPlans={floorPlans}
          localMappings={localMappings}
          onCameraClick={handleCameraClick}
          onCameraSelect={handleCameraSelect}
          onAddTempCamera={handleAddTempCamera}
          onMemoSelect={handleMemoSelect}
          onMemoSave={handleMemoSave}
          onToggleMemoOnFloorPlan={handleToggleMemoOnFloorPlan}
          openMemoSignal={memoOpenSignal}
          isAdjustmentActiveForSelected={
            selectedCameraId != null && adjustmentEditingCameraId === selectedCameraId
          }
          selectedCameraAdjustmentFrame={
            selectedCameraId != null
              ? effectiveAdjustment(selectedCameraId)?.frame ?? null
              : null
          }
          onAdjustmentFrameChange={handleAdjustmentFrameChange}
          onAdjustmentFrameChangeStart={pushUndo}
          onEndAdjustment={handleEndAdjustment}
          onCancelAdjustment={handleCancelAdjustment}
        />
      </LeftPanel>
      <RightPanel>
        <FloorPlanCanvas
          floorPlans={floorPlans}
          selectedFloorPlanId={selectedFloorPlanId}
          onFloorPlanChange={handleFloorPlanChange}
          camerasOnFloorPlan={visibleCamerasOnFloorPlan}
          selectedCameraIsUnplaced={selectedCameraIsUnplaced}
          cameraPositions={cameraPositions}
          selectedCameraId={selectedCameraId}
          onCameraMarkerClick={handleCameraMarkerClick}
          onCameraPlacedOnFloorPlan={handleCameraPlacedOnFloorPlan}
          nonAnalysisNumberMap={nonAnalysisNumberMap}
          iconScale={iconScale}
          onIconScaleChange={setIconScale}
          onIconScaleCommit={(v) => commitFloorPlanScale({ iconSize: v })}
          labelScale={labelScale}
          onLabelScaleChange={setLabelScale}
          onLabelScaleCommit={(v) => commitFloorPlanScale({ labelSize: v })}
          memosOnFloorPlan={memosOnFloorPlan}
          memoPlacingMode={memoPlacingMode}
          onMemoPlaced={handleMemoPlaced}
          onMemoMarkerClick={handleMemoMarkerClick}
          onMemoDragEnd={handleMemoDragEnd}
          selectedMemoId={selectedMemoId}
          onSelectionCleared={handleCanvasSelectionCleared}
          onDeleteCamera={handleDeleteCameraFromFloorPlan}
          onDeleteMemoFromFloorPlan={handleDeleteMemoFromFloorPlan}
          floorPlanMemoDiagrams={visibleDiagramsForCurrentFloor}
          floorPlanMemoTexts={visibleTextsForCurrentFloor}
          onMemoDiagramModified={handleMemoDiagramModified}
          onMemoTextModified={handleMemoTextModified}
          onMemoDiagramDelete={handleMemoDiagramDelete}
          onMemoTextDelete={handleMemoTextDelete}
          onMemoTextCreate={handleMemoTextCreate}
          onMemoDiagramCreate={handleMemoDiagramCreate}
          onMemoTextStyleChange={handleMemoTextStyleChange}
          onMemoDiagramStyleChange={handleMemoDiagramStyleChange}
          adjustments={effectiveAdjustments}
          onAdjustmentMoved={handleAdjustmentMoved}
          adjustmentEditingCameraId={adjustmentEditingCameraId}
          onStartAdjustment={handleStartAdjustment}
          onEndAdjustment={handleEndAdjustment}
          onAddMemo={handleAddMemoFromToolbar}
          topPadding={inlineMemoOverlayHeight}
          bottomPadding={bottomBarHeight}
        />
        {selectedCameraId && (
          <InlineMemoOverlay ref={inlineMemoOverlayRef}>
            <InlineMemoSection
              memos={effectiveMemos}
              selectedCameraId={selectedCameraId}
              selectedMemoId={selectedMemoId}
              onMemoIdChange={setSelectedMemoId}
              onSave={handleMemoSave}
              onToggleMemoPlacement={handleToggleMemoOnFloorPlan}
              placementLabels={{ add: t("memo.showOnFloorPlan"), remove: t("memo.removeFromFloorPlan") }}
              isPlacedOnContext={(m) =>
                m.floorPlanId === selectedFloorPlanId
                && selectedFloorPlanId != null
                && m.positionForFloorPlan != null
              }
            />
          </InlineMemoOverlay>
        )}
        <ActionBar
          ref={bottomBarRef}
          onSave={handleSave}
          onSaveAndAnalysis={handleSaveAndAnalysis}
          isSaving={isSaving}
          hasChanges={hasUnsavedChanges}
          uncommittedCameraNames={(() => {
            const ids = new Set<number>();
            // 1) pendingAdjustments 에 committed=false 인 모든 카메라
            for (const [cameraId, info] of pendingAdjustments) {
              if (info && !info.committed) ids.add(cameraId);
            }
            // 2) 현재 편집 중인 카메라 (혹시 pending 엔트리에 없는 edge 대비)
            if (adjustmentEditingCameraId != null) ids.add(adjustmentEditingCameraId);
            return Array.from(ids).map((id) => {
              const cam = cameras.find((c) => c.id === id);
              return cam?.analysisNumber ?? nonAnalysisNumberMap.get(id) ?? String(id);
            });
          })()}
        />
      </RightPanel>
      {placementConflict && (
        <ConfirmMoveCameraDialog
          fromName={floorPlans.find((fp) => fp.id === placementConflict.fromFloorPlanId)?.name ?? ""}
          toName={floorPlans.find((fp) => fp.id === selectedFloorPlanId)?.name ?? ""}
          onConfirm={handleConfirmMoveCameraPlacement}
          onCancel={handleCancelMoveCameraPlacement}
        />
      )}
      <DeleteConfirmDialog
        open={pendingMemoRemoval !== null}
        title={t("dialog.removeMemoFromFloorPlanTitle")}
        name=""
        messageKey=""
        message={t("dialog.removeMemoFromFloorPlanMessage")}
        ns="camera-mapping"
        onConfirm={handleConfirmMemoRemoval}
        onCancel={() => setPendingMemoRemoval(null)}
      />
      {isSaving && <SaveLoadingOverlay />}
    </Container>
  );
}

// --- Move camera (다른 도면 → 현재 도면) 확인 다이얼로그 ---
function ConfirmMoveCameraDialog({
  fromName,
  toName,
  onConfirm,
  onCancel,
}: {
  fromName: string;
  toName: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("camera-mapping");
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);
  return (
    <ConfirmDialog
      ref={dialogRef}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
    >
      <ConfirmHeader>{t("dialog.moveCameraTitle")}</ConfirmHeader>
      <ConfirmBody>{t("dialog.moveCameraMessage", { from: fromName, to: toName })}</ConfirmBody>
      <ConfirmFooter>
        <Button variant="outlined" color="inherit" size="medium" onClick={onCancel}>
          {t("action.cancel")}
        </Button>
        <Button variant="contained" color="primary" size="medium" onClick={onConfirm}>
          {t("action.move")}
        </Button>
      </ConfirmFooter>
    </ConfirmDialog>
  );
}

// --- Styles ---

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 480px;
  flex-shrink: 0;
  border-right: 1px solid rgba(143, 149, 178, 0.24);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 12px 12px 0 24px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const InlineMemoOverlay = styled.div`
  position: absolute;
  top: 48px;
  left: 12px;
  right: 12px;
  z-index: 10;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(143, 149, 178, 0.18);
  border-radius: 8px;
`;

const RightPanel = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const ConfirmDialog = styled.dialog`
  position: fixed;
  inset: 0;
  width: 480px;
  height: fit-content;
  margin: auto;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0px 20px 40px -4px rgba(105, 111, 140, 0.16);

  &::backdrop {
    background: rgba(17, 17, 31, 0.32);
  }
`;

const ConfirmHeader = styled.div`
  padding: 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const ConfirmBody = styled.div`
  padding: 0 24px;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.57;
  color: ${token("colors.text.primary")};
  white-space: pre-line;
`;

const ConfirmFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 24px;
`;
